import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { Badge } from "./badge";

const MultiSelect = React.forwardRef(
  (
    {
      options = [],
      selected = [],
      onChange,
      placeholder,
      allOption = null, // { value: '*', label: 'Tous' }
      className,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const resolvedPlaceholder = placeholder ?? t('Select...');
    const [open, setOpen] = React.useState(false);
    const containerRef = React.useRef(null);

    const isAllSelected = allOption && selected.includes(allOption.value);

    const handleToggle = (value) => {
      if (disabled) return;

      if (allOption && value === allOption.value) {
        // Toggle "all" option
        if (isAllSelected) {
          onChange([]);
        } else {
          onChange([allOption.value]);
        }
      } else {
        // Toggle individual option
        if (isAllSelected) {
          // If "all" was selected, switch to individual selection
          onChange([value]);
        } else if (selected.includes(value)) {
          onChange(selected.filter((v) => v !== value));
        } else {
          onChange([...selected, value]);
        }
      }
    };

    const handleRemove = (value, e) => {
      e.stopPropagation();
      if (disabled) return;
      onChange(selected.filter((v) => v !== value));
    };

    // Close dropdown when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event) => {
        if (containerRef.current && !containerRef.current.contains(event.target)) {
          setOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getDisplayValue = () => {
      if (isAllSelected && allOption) {
        return allOption.label;
      }
      if (selected.length === 0) {
        return null;
      }
      return null; // We'll show badges instead
    };

    const displayValue = getDisplayValue();
    const showBadges = !isAllSelected && selected.length > 0;

    return (
      <div ref={containerRef} className={cn("relative", className)} {...props}>
        <button
          ref={ref}
          type="button"
          onClick={() => !disabled && setOpen(!open)}
          className={cn(
            "flex min-h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring",
            disabled && "cursor-not-allowed opacity-50",
            !disabled && "cursor-pointer"
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {displayValue && (
              <span className="text-sm">{displayValue}</span>
            )}
            {showBadges && (
              selected.map((value) => {
                const option = options.find((o) => o.value === value);
                return (
                  <Badge
                    key={value}
                    variant="secondary"
                    className="text-xs px-1.5 py-0"
                  >
                    {option?.label || value}
                    <button
                      type="button"
                      onClick={(e) => handleRemove(value, e)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })
            )}
            {!displayValue && !showBadges && (
              <span className="text-muted-foreground">{resolvedPlaceholder}</span>
            )}
          </div>
          <ChevronDown className={cn("h-4 w-4 opacity-50 shrink-0 ml-2", open && "rotate-180 transition-transform")} />
        </button>

        {open && !disabled && (
          <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md">
            <div className="max-h-60 overflow-y-auto p-1">
              {allOption && (
                <>
                  <div
                    onClick={() => handleToggle(allOption.value)}
                    className={cn(
                      "relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                      isAllSelected && "bg-accent"
                    )}
                  >
                    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                      {isAllSelected && <Check className="h-4 w-4" />}
                    </span>
                    <span className="font-medium">{allOption.label}</span>
                  </div>
                  <div className="-mx-1 my-1 h-px bg-muted" />
                </>
              )}
              {options.map((option) => {
                const isSelected = selected.includes(option.value);
                const isDisabled = isAllSelected;

                return (
                  <div
                    key={option.value}
                    onClick={() => !isDisabled && handleToggle(option.value)}
                    className={cn(
                      "relative flex select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none",
                      isDisabled
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer hover:bg-accent hover:text-accent-foreground",
                      isSelected && !isDisabled && "bg-accent"
                    )}
                  >
                    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                      {isSelected && <Check className="h-4 w-4" />}
                    </span>
                    {option.label}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }
);

MultiSelect.displayName = "MultiSelect";

export { MultiSelect };
